import { Box, Typography, Paper, Avatar, TextField, Button, Divider, Snackbar, Alert, IconButton, InputAdornment } from '@mui/material';
import { useGlobalContext } from '../context/GlobalProvider';
import { UpdatePassword } from '../lib/appwrite';
import { useState, useEffect, useCallback } from 'react';
import UserData from '../components/data/UserData';
import LockIcon from '@mui/icons-material/Lock';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const Settings = () => {
    const { user } = useGlobalContext();
    const [userData, setUserData] = useState(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleCancel = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
    };

    const handleConfirm = async () => {
        if (newPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }
        if (newPassword.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres');
            return;
        }
        try {
            await UpdatePassword(currentPassword, newPassword);
            handleCancel();
            setSuccess(true);
        } catch (error) {
            setError('Error al actualizar la contraseña');
        }
    };

    const handleCloseSuccess = () => {
        setSuccess(false);
    };

    const fetchUserData = useCallback(async () => {
      if (user?.email) {
        const data = await UserData.fetch(user.email);
        setUserData(data);
      }
    }, [user?.email]);
  
    useEffect(() => {
      fetchUserData();
    }, [fetchUserData]);

    return (
        <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 3,
            gap: 3
          }}>
            <Box sx={{ 
                width: '100%',
                maxWidth: 1000
            }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Ajustes
                </Typography>
                <Paper 
                    elevation={3}
                    sx={{ 
                        p: 4,
                        borderRadius: 2,
                        width: '100%',
                        mb: 3
                    }}
                >
                    <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3
                    }}>
                        <Avatar
                            src={user?.avatar}
                            sx={{ 
                                width: 60, 
                                height: 60,
                                bgcolor: 'primary.light'
                            }}
                        >
                            {user?.email?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ textAlign: 'justify' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0 }}>
                                {userData ? `${userData.nombre} ${userData.apellidoPaterno} ${userData.apellidoMaterno}` : 'Usuario'}
                            </Typography>
                            <Typography variant="body3" color="text.secondary" sx={{ mb: 0.5 }}>
                                {user?.email || 'No email provided'}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                <Paper 
                    elevation={3}
                    sx={{ 
                        p: 4,
                        borderRadius: 2,
                        width: '100%',
                        mb: 3
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                        <LockIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Cambiar Contraseña
                        </Typography>
                    </Box>
                    <Typography variant="body3" color="text.secondary" sx={{ mb: 5 }}>
                        Actualiza tu contraseña para mantener tu cuenta segura
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, mt: 5 }}>
                        <TextField
                            label="Contraseña Actual"
                            type={showCurrentPassword ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            edge="end"
                                        >
                                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body3" color="text.secondary">
                            La contraseña debe de tener como minimo 8 caracteres.
                        </Typography>
                        <TextField
                            label="Nueva Contraseña"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />     
                        <TextField
                            label="Confirmar Nueva Contraseña"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            error={!!error}
                            helperText={error}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleCancel}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleConfirm}
                            disabled={!currentPassword || !newPassword || !confirmPassword}
                        >
                            Confirmar
                        </Button>
                    </Box>
                </Paper>

                <Paper 
                    elevation={3}
                    sx={{ 
                        p: 4,
                        borderRadius: 2,
                        width: '100%'
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Tips de Seguridad
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 2
                    }}>
                        <Typography variant="body3" color="text.secondary">
                            • Incluye una combinación de letras mayúsculas, minúsculas, números y símbolos.
                        </Typography>
                        <Typography variant="body3" color="text.secondary">
                            • Evita utilizar información personal en tu contraseña.
                        </Typography>
                        <Typography variant="body3" color="text.secondary">
                            • Considera activar la autenticación en dos pasos para mayor seguridad.
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            <Snackbar 
                open={success} 
                autoHideDuration={6000} 
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSuccess} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    Contraseña actualizada exitosamente
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Settings;